import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { AddUserNoteParams, useAddUserNoteMutation } from 'entities/User';
import { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { Button } from 'shared/ui/Button';
import { Drawer } from 'shared/ui/Drawer';
import { Paragraph } from 'shared/ui/Paragraph';
import { TextArea } from 'shared/ui/TextArea';
import { getUserNoteDrawerState } from '../model/selectors/getUserNoteDrawerState';
import { userNoteDrawerActions } from '../model/slice/userNoteDrawerSlice';
import { TabSwitcher } from 'shared/ui/TabSwitcher';
import { useGetLeadByUserIdQuery } from 'entities/Lead';
import { useGetUserAppealsQuery } from 'entities/Appeal';
import { getClientDateWithMinuteTimeFormat } from 'shared/utils/helpers/getDateFormat';
import { Divider } from 'antd';

type TabKey = 'lead' | 'user' | 'appeal';

export const UserNoteDrawer: FC = memo(() => {
  const dispatch = useAppDispatch();
  const { t } = useAppTranslation(['user', 'common']);

  const { user, isNote, isDebtCollectionNote } = useAppSelector(getUserNoteDrawerState);

  const [newNote, setNewNote] = useState<Nullable<string>>(null);
  const [selectedTabKey, setSelectedTabKey] = useState<TabKey>('user');

  const [addNote, { isLoading }] = useAddUserNoteMutation();
  const { data: lead } = useGetLeadByUserIdQuery(user?.userId, { skip: !user?.userId });
  const { data: appeals } = useGetUserAppealsQuery({ email: user?.email }, { skip: !user?.email });

  const closeUserNoteDrawer = useCallback((): void => {
    dispatch(userNoteDrawerActions.setOpenUserNoteDrawer({ isDebtCollectionNote: false, user: null, isNote: false }));
    setSelectedTabKey('user');
  }, [dispatch]);

  const handleChangeNote = useCallback((note: string) => {
    setNewNote(note);
  }, []);

  const tabs = useMemo(
    () => [
      { key: 'user', label: t('Notes') },
      { key: 'lead', label: t('Lead notes') },
      { key: 'appeal', label: t('Appeals') },
    ],
    [t],
  );

  const switchTab = useCallback((tabKey: string): void => {
    setSelectedTabKey(tabKey as TabKey);
  }, []);

  const handleAddUserNote = useCallback(async (): Promise<void> => {
    try {
      if (user && newNote) {
        const newNoteData: AddUserNoteParams = {};

        if (isDebtCollectionNote) {
          newNoteData.debtCollectionNote = newNote;
        }

        if (isNote) {
          newNoteData.note = newNote;
        }

        await addNote({ userId: user.userId, params: newNoteData }).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('User note added successfully'));

        closeUserNoteDrawer();

        setNewNote(null);
      }
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when adding user note'));
      console.log('error', error);
    }
  }, [user, newNote, isDebtCollectionNote, isNote, addNote, t, closeUserNoteDrawer]);

  const paragraphs = isDebtCollectionNote ? user?.debtCollectionNote?.split('\n') : user?.note?.split('\n');

  const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
    <p key={index} className="font-normal !mb-1">
      {paragraph}
    </p>
  ));

  const leadNotes = lead?.note?.split('\n');

  const leadParagraphElements = leadNotes?.map((paragraph: string, index: number) => (
    <p key={index} className="font-normal !mb-1">
      {paragraph}
    </p>
  ));

  const renderTabContent = useCallback((): Nullable<ReactNode> => {
    switch (selectedTabKey) {
      case 'user':
        return (
          <div className="flex flex-col gap-8">
            <div>
              <div className="mb-5">
                <div className="mb-2">{t('Note')}</div>
                <TextArea placeholder={t('Add your note...')} rows={4} bordered value={newNote} onChange={handleChangeNote} />
              </div>
              <Button onClick={handleAddUserNote} isLoading={isLoading} isDisabled={!user || !newNote}>
                {t('Add Note')}
              </Button>
            </div>

            <div>
              <div className="mb-2">{t('Note history')}:</div>
              <Paragraph expandable={false} className="whitespace-normal">
                {paragraphElements}
              </Paragraph>
            </div>
          </div>
        );

      case 'lead':
        if (!leadParagraphElements) {
          return t('No lead note history yet!');
        }

        return (
          <div>
            <div className="mb-2">{t('Note history')}:</div>
            <Paragraph expandable={false} className="whitespace-normal">
              {leadParagraphElements}
            </Paragraph>
          </div>
        );

      case 'appeal':
        if (appeals?.content.length === 0) {
          return t('No appeals yet!');
        }

        return (
          <div>
            <div className="mb-2">{t('Appeals')}:</div>
            {appeals?.content.map((appeal) => (
              <div key={appeal.appealId} className="my-2 flex flex-col gap-1">
                <div className="font-normal">
                  {t('Date')}: {getClientDateWithMinuteTimeFormat(appeal.createdAt)}
                </div>

                <div className="flex flex-col font-normal text-primary">
                  <div className=" font-normal">
                    {t('Question')}: {appeal.note}
                  </div>
                </div>
                <Divider className="my-1" />
              </div>
            ))}
          </div>
        );
      default:
        return null;
    }
  }, [
    appeals?.content,
    handleAddUserNote,
    handleChangeNote,
    isLoading,
    leadParagraphElements,
    newNote,
    paragraphElements,
    selectedTabKey,
    t,
    user,
  ]);

  return (
    <Drawer width={450} open={Boolean(user)} onClose={closeUserNoteDrawer}>
      <div className="flex flex-col px-10 py-4">
        <div className="flex justify-between items-center py-4">
          <div className="font-semibold text-3xl">{t(isDebtCollectionNote ? 'Debt Collection Notes' : 'Notes')}</div>
          <div className="text-primaryLight font-normal cursor-pointer" onClick={closeUserNoteDrawer}>
            {t('Close', { ns: 'common' })}
          </div>
        </div>
        <TabSwitcher tabs={tabs} selectedTabKey={selectedTabKey} onChangeTab={switchTab}>
          {renderTabContent()}
        </TabSwitcher>
      </div>
    </Drawer>
  );
});
